import React, { useEffect, useRef, useState } from 'react';
import {
  getMezzi, getIngressiUscite, createIngressoUscita,
  updateIngressoUscita, deleteIngressoUscita,
  exportIngressiXlsx, importIngressiXlsx,
  Mezzo, IngressoUscita,
} from '../../services/fleet';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const EMPTY: Partial<IngressoUscita> = {
  data_uscita: new Date().toISOString().split('T')[0],
};

export default function IngressiUscite() {
  const [mezzi, setMezzi]             = useState<Mezzo[]>([]);
  const [records, setRecords]         = useState<IngressoUscita[]>([]);
  const [loading, setLoading]         = useState(true);
  const [filterMezzo, setFilterMezzo] = useState<number | ''>('');
  const [dataDa, setDataDa]           = useState('');
  const [dataA, setDataA]             = useState('');
  const [showForm, setShowForm]       = useState(false);
  const [editing, setEditing]         = useState<IngressoUscita | null>(null);
  const [form, setForm]               = useState<Partial<IngressoUscita>>(EMPTY);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [importing, setImporting]     = useState(false);
  const importRef                     = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      const params: Record<string, unknown> = {};
      if (filterMezzo) params.mezzo_id = filterMezzo;
      if (dataDa)      params.data_da  = dataDa;
      if (dataA)       params.data_a   = dataA;
      const res = await exportIngressiXlsx(params);
      downloadBlob(res.data as Blob, 'fleet_ingressi.xlsx');
    } catch { setError('Errore export'); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importIngressiXlsx(file);
      const { created, updated, errors } = res.data;
      alert(`Import completato: ${created} creati, ${updated} aggiornati${errors.length ? '\nErrori: ' + errors.join('\n') : ''}`);
      load();
    } catch { setError('Errore import'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  useEffect(() => {
    getMezzi(true).then(r => setMezzi(r.data));
    load();
  }, []);

  useEffect(() => { load(); }, [filterMezzo, dataDa, dataA]);

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (filterMezzo) params.mezzo_id = filterMezzo;
      if (dataDa)      params.data_da  = dataDa;
      if (dataA)       params.data_a   = dataA;
      const res = await getIngressiUscite(params);
      setRecords(res.data);
    } catch {
      setError('Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, mezzo_id: filterMezzo || undefined });
    setShowForm(true);
    setError('');
  };

  const openEdit = (r: IngressoUscita) => {
    setEditing(r);
    setForm({ ...r });
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.mezzo_id)    { setError('Seleziona un veicolo'); return; }
    if (!form.data_uscita) { setError('Data uscita obbligatoria'); return; }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateIngressoUscita(editing.id, form);
      } else {
        await createIngressoUscita(form);
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Eliminare questo record?')) return;
    try { await deleteIngressoUscita(id); load(); } catch { setError('Errore eliminazione'); }
  };

  const set = (patch: Partial<IngressoUscita>) => setForm(f => ({ ...f, ...patch }));

  const totKm    = records.reduce((s, r) => s + (r.km_percorsi || 0), 0);
  const inUscita = records.filter(r => !r.data_rientro).length;

  return (
    <div className="fleet-section">
      <div className="fleet-toolbar">
        <select className="fleet-filter" value={filterMezzo} onChange={e => setFilterMezzo(parseInt(e.target.value) || '')}>
          <option value="">Tutti i veicoli</option>
          {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
        </select>
        <input type="date" className="fleet-filter" value={dataDa} onChange={e => setDataDa(e.target.value)} />
        <input type="date" className="fleet-filter" value={dataA}  onChange={e => setDataA(e.target.value)} />
        <button className="btn-secondary" onClick={handleExport} title="Scarica XLSX">⬇ XLSX</button>
        <button className="btn-secondary" onClick={() => importRef.current?.click()} disabled={importing} title="Carica XLSX">
          {importing ? '...' : '⬆ XLSX'}
        </button>
        <input ref={importRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImport} />
        <button className="btn-primary" onClick={openNew}>+ Registra Uscita</button>
      </div>

      {(totKm > 0 || inUscita > 0) && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: '0.875rem' }}>
          {totKm > 0 && (
            <span style={{ color: '#374151' }}>
              KM totali periodo: <strong>{totKm.toLocaleString()} km</strong>
            </span>
          )}
          {inUscita > 0 && (
            <span style={{ background: '#fef9c3', color: '#854d0e', padding: '2px 10px', borderRadius: 6, fontWeight: 600 }}>
              ⚠️ {inUscita} veicolo/i ancora in uscita
            </span>
          )}
        </div>
      )}

      {error && <div className="fleet-error">{error}</div>}

      {loading ? (
        <div className="fleet-loading">Caricamento...</div>
      ) : (
        <table className="fleet-table">
          <thead>
            <tr>
              <th>Veicolo</th>
              <th>Autista</th>
              <th>Data uscita</th>
              <th>KM uscita</th>
              <th>Data rientro</th>
              <th>KM rientro</th>
              <th>KM percorsi</th>
              <th>Destinazione</th>
              <th>Note</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>Nessun record</td></tr>
            ) : records.map(r => (
              <tr key={r.id} style={{ background: !r.data_rientro ? '#fffbeb' : 'white' }}>
                <td><strong>{r.mezzo_targa}</strong></td>
                <td>{r.autista_nome || '—'}</td>
                <td>{new Date(r.data_uscita).toLocaleDateString('it-IT')}</td>
                <td>{r.km_uscita  ? r.km_uscita.toLocaleString()  : '—'}</td>
                <td>
                  {r.data_rientro
                    ? new Date(r.data_rientro).toLocaleDateString('it-IT')
                    : <span style={{ color: '#f59e0b', fontWeight: 600 }}>In uscita</span>}
                </td>
                <td>{r.km_rientro ? r.km_rientro.toLocaleString() : '—'}</td>
                <td>{r.km_percorsi ? <strong>{r.km_percorsi.toLocaleString()} km</strong> : '—'}</td>
                <td>{r.destinazione || '—'}</td>
                <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>{r.note || '—'}</td>
                <td>
                  <button className="btn-icon" onClick={() => openEdit(r)}>✏️</button>
                  <button className="btn-icon btn-danger" onClick={() => handleDelete(r.id)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="fleet-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="fleet-modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
            <div className="fleet-modal-header">
              <h2>{editing ? 'Modifica Uscita/Rientro' : 'Registra Uscita Veicolo'}</h2>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="fleet-modal-body">
              {error && <div className="fleet-error">{error}</div>}

              <div className="form-section-title">Uscita</div>
              <div className="form-grid-3">
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Veicolo *</label>
                  <select value={form.mezzo_id || ''} onChange={e => set({ mezzo_id: parseInt(e.target.value) || undefined })}>
                    <option value="">— Seleziona —</option>
                    {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Data uscita *</label>
                  <input type="date" value={form.data_uscita || ''} onChange={e => set({ data_uscita: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>KM uscita</label>
                  <input type="number" value={form.km_uscita || ''} onChange={e => set({ km_uscita: parseInt(e.target.value) || undefined })} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Destinazione</label>
                  <input value={form.destinazione || ''} onChange={e => set({ destinazione: e.target.value })} />
                </div>
              </div>

              <div className="form-section-title">Rientro (compilare al rientro)</div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label>Data rientro</label>
                  <input type="date" value={form.data_rientro || ''} onChange={e => set({ data_rientro: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>KM rientro</label>
                  <input type="number" value={form.km_rientro || ''} onChange={e => set({ km_rientro: parseInt(e.target.value) || undefined })} />
                </div>
                <div className="form-group">
                  <label>Note</label>
                  <input value={form.note || ''} onChange={e => set({ note: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="fleet-modal-footer">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Annulla</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : (editing ? 'Aggiorna' : 'Salva')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
